/**
 * Image viewer.
 *
 * Pixels come from the core's RGBA buffer, not from an `<img>` pointing at a
 * blob: the same buffer the edit operations work on is the one drawn, so what
 * is on screen is exactly what a save would produce. There is no second
 * decoding path that could disagree with the first.
 */
import { useEffect, useMemo, useRef } from 'react';
import { renderImage } from '@moduviewer/handlers';
import type { ImageModel } from '@moduviewer/handlers';

export interface ImageViewerProps {
  readonly model: ImageModel;
  /** 1 = actual size; null = fit to the available space. */
  readonly zoom: number | null;
}

export function ImageViewer({ model, zoom }: ImageViewerProps): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Re-rendering the edit list is cheap for a viewer-sized image and keeps a
  // single source of truth; memoising on the cursor avoids doing it per frame.
  const image = useMemo(() => renderImage(model), [model]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext('2d');
    if (!context) return;
    // Let the context allocate the ImageData and copy into it, rather than
    // wrapping the model's buffer. The buffer belongs to the edit pipeline and
    // handing the canvas a view onto it invites an aliasing bug the moment a
    // worker is added.
    const data = context.createImageData(image.width, image.height);
    data.data.set(image.data);
    context.putImageData(data, 0, 0);
  }, [image]);

  const style = zoom === null
    ? undefined
    : {
      width: image.width * zoom,
      height: image.height * zoom,
      // Nearest-neighbour above 200%: at that magnification a user is
      // inspecting individual pixels, and smoothing hides what they came for.
      imageRendering: zoom >= 2 ? ('pixelated' as const) : ('auto' as const),
    };

  return (
    <div className="canvas image-view">
      <canvas
        ref={canvasRef}
        style={style}
        data-actual={zoom !== null ? 'true' : 'false'}
        role="img"
        aria-label={`${image.width} × ${image.height}`}
      />
    </div>
  );
}
